import React from 'react';
import { useTranslation } from 'react-i18next';

import CustomAlert from '../../../../UiComponentsLibrary/CustomAlert';
import { CustomIconButton } from '../../../../UiComponentsLibrary/CustomIconButton';
import ReusableTable from '../../../../Shared/components/ReusableTable';
import { UserInterface } from '../../../../Shared/interfaces/user/UserInterfaces';
import { getFormattedDate } from '../../../../Shared/utils/helpers';
import { dataTableActions } from '../../../config/actionButtonsConfig';
import { DataTableActionsFlex } from '../UserManagement.styles';
import {
  EXCLUDED_COLUMNS,
  USER_ROLE_FIELD,
  USER_TABLE_HEADERS,
  PRIVILEGED_MODE_FIELD,
} from '../../../constants/userTableConstants';
import { USER_STATUS } from '../../../constants/userStatus';
import { ACTION_TYPES } from '../../../constants/actionsTypes';
import { DATE_FIELDS } from '../../../constants/userTableConstants';
import {
  ROLE_ENUM_TO_LABEL_MAP,
  UserRoles,
} from '../../../../Shared/constants/userRoles';
import { canUserEdit } from '../../../../Shared/utils/helpers';
import { useAppContext } from '../../../../Shared/store/appContext';

interface TableProps {
  usersData: UserInterface[];
  handleDataTableAction: (actionType: string, row: UserInterface) => void;
}

interface RowTableProps {
  handleDataTableAction: (actionType: string, row: UserInterface) => void;
  row: UserInterface;
  canEdit: boolean;
}

const ActionButtons = ({ row, handleDataTableAction, canEdit }: RowTableProps) => {

  return (
    <DataTableActionsFlex id="user-management-data-table-actions-container">
      {row.userStatus === USER_STATUS.DELETED ? (
        <CustomIconButton
          icon={dataTableActions.restoreUser.icon}
          backgroundColor={dataTableActions.restoreUser.backgroundColor}
          label=""
          disabled={!canEdit}
          handleOnClickCb={() =>
            handleDataTableAction(ACTION_TYPES.RESTORE_USER, row)
          }
        />
      ) : (
        Object.entries(dataTableActions)
          .filter(([action]) => action !== ACTION_TYPES.RESTORE_USER)
          .map(([action, value]) => (
            <div key={action} id={`user-action-${action}-${row.userId}`}>
              <CustomIconButton
                icon={value.icon}
                backgroundColor={value.backgroundColor}
                label=""
                disabled={action !== ACTION_TYPES.VIEW_USER && !canEdit}
                handleOnClickCb={() => handleDataTableAction(action, row)}
              />
            </div>
          ))
      )}
    </DataTableActionsFlex>
  );
};

const TableRow = ({ row, handleDataTableAction, canEdit }: RowTableProps) => {
  const { t } = useTranslation();
  return (
    <>
      <td id="action-buttons-cell">
        <ActionButtons
          row={row}
          handleDataTableAction={handleDataTableAction}
          canEdit={canEdit}
        />
      </td>
      {Object.keys(row)
        .filter((key) => !EXCLUDED_COLUMNS.includes(key))
        .map((key) => {
          const value = row[key as keyof UserInterface];

          if (key === PRIVILEGED_MODE_FIELD) {
            return (
              <td id={`user-management-table-row-${key}`} key={key}>
                {t(value ? 'privileged-mode-on' : 'privileged-mode-off')}
              </td>
            );
          }

          // Handle null values
          if (value === null || value === undefined) {
            return (
              <td id={`user-management-table-row-${key}`} key={key}>
                {t('not-available-user-table-label')}
              </td>
            );
          }

          // Handle Unix timestamps
          if (DATE_FIELDS.includes(key as (typeof DATE_FIELDS)[number])) {
            const numValue = typeof value === 'string' ? Number(value) : value;
            return (
              <td id={`user-management-table-row-${key}`} key={key}>
                {getFormattedDate(numValue)}
              </td>
            );
          }

          let displayValue = value;

          if (USER_ROLE_FIELD === key) {
            const roleOption = ROLE_ENUM_TO_LABEL_MAP.find(
              (option) => option.role === (value as UserRoles)
            );
            displayValue = roleOption ? t(roleOption.label) : value;
          }

          return (
            <td id={`user-management-table-row-${key}`} key={key}>
              {displayValue}
            </td>
          );
        })}
    </>
  );
};

const UsersTable = ({ usersData, handleDataTableAction }: TableProps) => {
  const { t } = useTranslation();
  const { state } = useAppContext();
  const canEdit = canUserEdit(state.currentUser?.permissions);

  return (
    <div id="user-management-table">
      <ReusableTable
        tableHeaders={[
          t('user-management-actions-table-header'),
          ...Object.keys(usersData[0])
            .filter((key) => !EXCLUDED_COLUMNS.includes(key))
            .map((str) =>
              t(USER_TABLE_HEADERS[str as keyof typeof USER_TABLE_HEADERS])
            ),
        ]}
        rowsData={usersData}
        renderRow={(row) => (
          <TableRow row={row} handleDataTableAction={handleDataTableAction} canEdit={canEdit} />
        )}
      />
    </div>
  );
};

const TableOrNoData = ({ usersData, handleDataTableAction }: TableProps) => {
  const { t } = useTranslation();

  if (usersData.length === 0)
    return (
      <CustomAlert alertMessage={t('table-no-data-label')} variant="info" />
    );

  return (
    <UsersTable
      usersData={usersData}
      handleDataTableAction={handleDataTableAction}
    />
  );
};

export default TableOrNoData;
